import { useEffect, useRef, useState, type ReactNode } from 'react';
import type { ConfigPapel, Encadernacao, Formato } from '@miolos/core';
import { PASSO_FURO_WIRE_O_MM, posicoesDosFuros, ladoEfetivo, medirPagina } from '@miolos/core';

const PX_POR_MM = 96 / 25.4;

/** Largura (ou altura, na encadernação de topo) que a encadernação ocupa. */
const FAIXA_MM: Record<Encadernacao, number> = {
  NENHUMA: 0,
  WIRE_O: 6,
  ESPIRAL: 6,
  CAPA_DURA: 12,
};

/** Observa a largura de um elemento (usado para escalar a pré-visualização). */
export function useLargura<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  const [largura, setLargura] = useState(0);

  useEffect(() => {
    const elemento = ref.current;
    if (!elemento) return;

    setLargura(elemento.clientWidth);
    const observador = new ResizeObserver((entradas) => {
      for (const entrada of entradas) {
        setLargura(entrada.contentRect.width);
      }
    });
    observador.observe(elemento);
    return () => observador.disconnect();
  }, []);

  return { ref, largura };
}

interface Props {
  formato: Formato;
  children: ReactNode;
  /** Escala máxima (1 = tamanho real na tela). */
  escalaMaxima?: number;
  className?: string;
  sombra?: boolean;
  /** Orientação e margem do miolo (retrato/paisagem). */
  papel?: ConfigPapel;
  /**
   * Multiplicador de zoom da prévia.
   *
   * Em tela estreita a página encolhe muito (A4 num celular fica perto de 0,37)
   * e o texto de 9 pt vira borrão. Com zoom acima de 1 a página passa a ser
   * maior que a largura disponível — quem cuida da rolagem é o container.
   */
  fatorZoom?: number;
  /** Página par? Decide de que lado fica a lombada espelhada. */
  indicePar?: boolean;
}

/**
 * Encadernação desenhada ao lado da folha.
 *
 * É representação: não sai na impressão. Serve para o cliente ver como o miolo
 * vai ficar na mão — arame, espiral ou lombada de capa dura — antes de mandar
 * encadernar.
 */
function FaixaDeEncadernacao({
  encadernacao,
  alturaMm,
  horizontal,
}: {
  encadernacao: Encadernacao;
  alturaMm: number;
  horizontal: boolean;
}) {
  const largura = FAIXA_MM[encadernacao];
  if (largura === 0) return null;

  /*
   * Medida explícita em milímetros: com `height: 100%` a fita pegava a altura da
   * linha flexível, que é automática, e acabava com altura zero — o arame ainda
   * aparecia (posicionado em milímetros), mas a lombada da capa dura, que é
   * fundo, não.
   */
  const medida = horizontal
    ? { width: `${alturaMm}mm`, height: `${largura}mm` }
    : { width: `${largura}mm`, height: `${alturaMm}mm` };

  if (encadernacao === 'CAPA_DURA') {
    return (
      <div
        style={{
          ...medida,
          flex: '0 0 auto',
          background: 'linear-gradient(90deg, #2f3033, #4b4d52 45%, #2f3033)',
          boxShadow: 'inset 0 0 0 0.2mm rgba(255,255,255,0.12)',
          borderRadius: horizontal ? '0 0 1mm 1mm' : '1mm 0 0 1mm',
        }}
        aria-hidden
      />
    );
  }

  // Wire-o e espiral: o arame atravessa a borda a cada passo de furo.
  const centros = posicoesDosFuros(alturaMm, PASSO_FURO_WIRE_O_MM);

  return (
    <div style={{ ...medida, flex: '0 0 auto', position: 'relative' }} aria-hidden>
      {centros.map((centro, indice) => {
        const arame = encadernacao === 'WIRE_O';
        return (
          <span
            key={indice}
            style={{
              position: 'absolute',
              top: `${centro}mm`,
              left: '50%',
              width: arame ? '5.2mm' : '4.6mm',
              height: arame ? '3.4mm' : '2.6mm',
              transform: `translate(-50%, -50%) rotate(${arame ? 0 : 24}deg)`,
              borderRadius: '999px',
              border: arame ? '0.45mm solid #b6bcc4' : '0.5mm solid #4b4b52',
              background: arame
                ? 'linear-gradient(180deg, #f2f4f7, #aeb5be 55%, #7d838b)'
                : 'linear-gradient(180deg, #5a5a63, #2f2f35)',
              boxShadow: '0 0.2mm 0.4mm rgba(15,23,42,0.25)',
            }}
          />
        );
      })}
    </div>
  );
}

/**
 * Mostra uma página do miolo ajustada à largura disponível.
 * A página é desenhada em tamanho real e reduzida com `transform: scale()`,
 * então o que você vê é exatamente o que sai na impressão.
 */
export function MolduraPrevia({
  formato,
  children,
  escalaMaxima = 1,
  className,
  sombra = true,
  papel,
  fatorZoom = 1,
  indicePar = false,
}: Props) {
  const { ref, largura } = useLargura<HTMLDivElement>();
  const medidas = medirPagina(formato, { orientacao: papel?.orientacao });
  const encadernacao = papel?.encadernacao ?? 'NENHUMA';
  const faixa = FAIXA_MM[encadernacao];

  const lado = ladoEfetivo(papel, indicePar);
  const topo = papel?.ladoLombada === 'TOPO';
  const antes = topo || lado === 'ESQUERDO';
  const depois = !topo && lado === 'DIREITO';

  const faixaAltura = topo ? faixa : 0;
  const larguraNecessaria = (medidas.larguraMm + (topo ? 0 : faixa)) * PX_POR_MM;
  const alturaNecessaria = (medidas.alturaMm + faixaAltura) * PX_POR_MM;
  const escala = largura > 0 ? Math.min(escalaMaxima, largura / larguraNecessaria) * fatorZoom : 0;

  return (
    <div ref={ref} className={className} style={{ width: '100%' }}>
      {escala > 0 ? (
        <div
          style={{
            width: larguraNecessaria * escala,
            height: alturaNecessaria * escala,
            margin: '0 auto',
            position: 'relative',
          }}
        >
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              transform: `scale(${escala})`,
              transformOrigin: 'top left',
              display: 'flex',
              flexDirection: topo ? 'column' : 'row',
              alignItems: 'stretch',
            }}
          >
            {antes ? (
              <FaixaDeEncadernacao
                encadernacao={encadernacao}
                alturaMm={topo ? medidas.larguraMm : medidas.alturaMm}
                horizontal={topo}
              />
            ) : null}

            <div
              style={{
                width: `${medidas.larguraMm}mm`,
                height: `${medidas.alturaMm}mm`,
                position: 'relative',
                flex: '0 0 auto',
                background: '#fff',
                boxShadow: sombra
                  ? '0 10px 40px -12px rgba(15, 23, 42, 0.35), 0 0 0 1px rgba(15, 23, 42, 0.06)'
                  : undefined,
                borderRadius: '2px',
                overflow: 'hidden',
              }}
            >
              {children}
            </div>

            {depois ? (
              <FaixaDeEncadernacao
                encadernacao={encadernacao}
                alturaMm={medidas.alturaMm}
                horizontal={false}
              />
            ) : null}
          </div>
        </div>
      ) : (
        <div
          style={{
            aspectRatio: `${medidas.larguraMm + (topo ? 0 : faixa)} / ${medidas.alturaMm + faixaAltura}`,
          }}
          className="rounded bg-muted"
        />
      )}
    </div>
  );
}
